import wsService from 'services/websocket/WebSocketService';
import { updateSession } from 'services/session/session';

/**
 * LockingService - Handles resource locking logic across modules
 * This service manages WebSocket subscriptions and lock state for any module
 */
class LockingService {
	constructor(module, onLockUpdate) {
		this.module = module;
		this.onLockUpdate = onLockUpdate; // Callback when locks are updated
		this.unsubscribe = null;
		this.lockedResourceIDs = [];
	}

	/**
	 * Parse locked resource IDs from WebSocket reference string
	 */
	parseLockedIDs(reference) {
		try {
			// Try JSON parsing first
			try {
				return JSON.parse(reference);
			} catch {
				// Manual parsing: remove brackets and split
				const cleanRef = reference.replace(/[\[\]]/g, '').trim();
				return cleanRef ? cleanRef.split(',').map(id => id.trim()) : [];
			}
		} catch {
			return [reference];
		}
	}

	/**
	 * Handle incoming WebSocket LOCK messages
	 */
	handleWebSocketMessage = (message) => {
		if (message.EVENT === 'LOCK' && message.MODULE === this.module) {
			const currentClientId = wsService.getClientId();

			if (message.SENDTO === currentClientId && Array.isArray(message.VALUES)) {
				const lockedIDs = [];

				message.VALUES.forEach((value) => {
					if (Array.isArray(value.REFERENCE_CD)) {
						value.REFERENCE_CD.forEach((ref) => {
							// Skip own session and special references
							if (
								ref.sessionID !== currentClientId &&
								ref.reference !== 'LOGIN' &&
								ref.reference !== 'INITIALIZED'
							) {
								const parsedRef = this.parseLockedIDs(ref.reference);
								if (Array.isArray(parsedRef)) {
									const ids = parsedRef.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
									lockedIDs.push(...ids);
								} else {
									const requestId = parseInt(parsedRef, 10);
									if (!isNaN(requestId)) lockedIDs.push(requestId);
								}
							}
						});
					}
				});

				this.lockedResourceIDs = [...new Set(lockedIDs)];

				// Notify the component about the update
				if (this.onLockUpdate) {
					this.onLockUpdate(this.lockedResourceIDs);
				}
			}
		}
	};

	/**
	 * Subscribe to WebSocket messages
	 */
	subscribe() {
		this.unsubscribe = wsService.subscribe(this.handleWebSocketMessage);
	}

	/**
	 * Unsubscribe from WebSocket messages
	 */
	unsubscribe() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
	}

	/**
	 * Check if a resource ID is locked
	 */
	isResourceLocked(resourceId) {
		const resourceIdNum = parseInt(resourceId, 10);
		return this.lockedResourceIDs.includes(resourceIdNum);
	}

	/**
	 * Update session with expanded resource IDs
	 */
	static updateSessionLocks(resourceIDs) {
		updateSession(resourceIDs);
	}

	/**
	 * Get locked resource IDs
	 */
	getLockedResourceIDs() {
		return this.lockedResourceIDs;
	}
}

export default LockingService;
